import { useState, useRef, useCallback, useEffect } from 'react';
import {
  presignUpload,
  completeUpload,
  createMultipartUpload,
  getMultipartPartUrls,
  completeMultipartUpload,
  abortMultipartUpload,
} from '@/lib/api';
import {
  ALLOWED_IMAGE_TYPES,
  ALLOWED_VIDEO_TYPES,
  ALLOWED_AUDIO_TYPES,
  MAX_FILE_SIZE_BYTES,
  MULTIPART_THRESHOLD_BYTES,
  MULTIPART_MAX_CONCURRENT_PARTS,
  UPLOAD_MAX_CONCURRENT_FILES,
  UPLOAD_MAX_RETRIES,
  IMAGE_MAX_UPLOAD_DIMENSION,
} from '@goodchat/shared/constants';
import { toast } from '@/stores/toast.store';

interface UploadingFile {
  id: string;
  file: File;
  progress: number;
  attachmentId?: string;
  preview?: string;
  error?: string;
}

interface FileUploadProps {
  onAttachmentsReady: (attachmentIds: string[]) => void;
  onCancel: () => void;
  uploads: UploadingFile[];
  setUploads: React.Dispatch<React.SetStateAction<UploadingFile[]>>;
}

const ALLOWED_TYPES = [
  ...ALLOWED_IMAGE_TYPES,
  ...ALLOWED_VIDEO_TYPES,
  ...ALLOWED_AUDIO_TYPES,
] as readonly string[];

function formatMaxSize(): string {
  const gb = MAX_FILE_SIZE_BYTES / (1024 * 1024 * 1024);
  if (gb >= 1) return `${Math.round(gb)}GB`;
  return `${Math.round(MAX_FILE_SIZE_BYTES / (1024 * 1024))}MB`;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Retry an async op with exponential backoff. `fn` receives the attempt index (0-based). */
async function withRetry<T>(fn: (attempt: number) => Promise<T>, retries: number): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn(attempt);
    } catch (err) {
      lastErr = err;
      if (attempt < retries) await sleep(500 * Math.pow(2, attempt));
    }
  }
  throw lastErr;
}

/**
 * PUT a blob to a presigned S3 URL via XHR so we get real byte-level upload
 * progress (fetch() cannot report upload progress). Returns the ETag response
 * header (needed for multipart completion), or null if not exposed.
 * Pass contentType=null for multipart parts (their presigned URL is not signed
 * with a Content-Type, so sending one can trip strict S3-compatible providers).
 */
function xhrPut(
  url: string,
  body: Blob,
  contentType: string | null,
  onProgress: (loaded: number, total: number) => void,
): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.getResponseHeader('ETag'));
      } else {
        reject(new Error(`Upload failed (HTTP ${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.ontimeout = () => reject(new Error('Upload timed out'));
    xhr.onabort = () => reject(new Error('Upload aborted'));
    xhr.send(body);
  });
}

/** Run tasks over `items` with bounded concurrency. */
async function runPool<T>(
  items: T[],
  worker: (item: T) => Promise<void>,
  concurrency: number,
): Promise<void> {
  let index = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (index < items.length) {
      const current = index++;
      await worker(items[current]);
    }
  });
  await Promise.all(runners);
}

/** Decode an image file's pixel dimensions (best-effort; undefined for HEIC/non-images). */
async function getImageDimensions(file: File): Promise<{ width: number; height: number } | undefined> {
  if (!file.type.startsWith('image/')) return undefined;
  try {
    const bitmap = await createImageBitmap(file);
    const dims = { width: bitmap.width, height: bitmap.height };
    bitmap.close?.();
    return dims;
  } catch {
    return undefined;
  }
}

/**
 * Downscale large photos in the browser before upload so sends are fast and
 * storage stays small. Preserves format for PNG/WebP (transparency) and leaves
 * GIF/HEIC untouched (animation / undecodable). Returns the original if it's
 * already small enough or if anything goes wrong.
 */
async function maybeDownscaleImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  if (file.type === 'image/gif' || file.type === 'image/heic' || file.type === 'image/heif') {
    return file;
  }
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const longest = Math.max(bitmap.width, bitmap.height);
    if (longest <= IMAGE_MAX_UPLOAD_DIMENSION) {
      bitmap.close?.();
      return file;
    }
    const scale = IMAGE_MAX_UPLOAD_DIMENSION / longest;
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const outType =
      file.type === 'image/png' ? 'image/png' : file.type === 'image/webp' ? 'image/webp' : 'image/jpeg';
    const quality = outType === 'image/jpeg' ? 0.85 : undefined;
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, outType, quality));
    if (!blob || blob.size >= file.size) return file;

    const newName = file.name.replace(/\.[^.]+$/, outType === 'image/png' ? '.png' : outType === 'image/webp' ? '.webp' : '.jpg');
    return new File([blob], newName, { type: outType });
  } catch {
    return file;
  }
}

/** Small file: single presigned PUT with progress + retry. Returns attachmentId. */
async function uploadSinglePut(file: File, onProgress: (pct: number) => void): Promise<string> {
  const { uploadUrl, fileKey, attachmentId } = await presignUpload({
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
    context: 'attachment',
  });

  await withRetry(
    () => xhrPut(uploadUrl, file, file.type, (loaded, total) => onProgress(5 + (loaded / total) * 85)),
    UPLOAD_MAX_RETRIES,
  );

  const dims = await getImageDimensions(file);
  await completeUpload(attachmentId, { fileKey, width: dims?.width, height: dims?.height });
  return attachmentId;
}

/** Large file: S3 multipart — parallel parts, per-part retry, abort on failure. */
async function uploadMultipart(file: File, onProgress: (pct: number) => void): Promise<string> {
  const { attachmentId, fileKey, uploadId, partSize, partCount } = await createMultipartUpload({
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
    context: 'attachment',
  });

  try {
    const partNumbers = Array.from({ length: partCount }, (_, i) => i + 1);
    const { urls } = await getMultipartPartUrls({ fileKey, uploadId, partNumbers });
    const urlByPart = new Map(urls.map((u) => [u.partNumber, u.url]));

    const loadedByPart = new Array<number>(partCount + 1).fill(0);
    const parts: { partNumber: number; etag: string }[] = [];

    await runPool(
      partNumbers,
      async (partNumber) => {
        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const blob = file.slice(start, end);
        const url = urlByPart.get(partNumber);
        if (!url) throw new Error(`Missing upload URL for part ${partNumber}`);

        const etag = await withRetry(
          () =>
            xhrPut(url, blob, null, (loaded) => {
              loadedByPart[partNumber] = loaded;
              const sum = loadedByPart.reduce((a, b) => a + b, 0);
              onProgress(5 + (sum / file.size) * 85);
            }),
          UPLOAD_MAX_RETRIES,
        );
        if (!etag) throw new Error(`Missing ETag for part ${partNumber}`);
        parts.push({ partNumber, etag });
      },
      MULTIPART_MAX_CONCURRENT_PARTS,
    );

    const dims = await getImageDimensions(file);
    await completeMultipartUpload(attachmentId, {
      fileKey,
      uploadId,
      parts,
      width: dims?.width,
      height: dims?.height,
    });
    return attachmentId;
  } catch (err) {
    // Discard the in-flight upload so S3 doesn't retain orphaned parts.
    await abortMultipartUpload({ fileKey, uploadId }).catch(() => {});
    throw err;
  }
}

export function useFileUpload() {
  const [uploads, setUploads] = useState<UploadingFile[]>([]);
  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;

  // Revoke all object URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      uploadsRef.current.forEach((u) => {
        if (u.preview) URL.revokeObjectURL(u.preview);
      });
    };
  }, []);

  const processFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const newUploads: UploadingFile[] = [];
    const rejections: { name: string; reason: string }[] = [];
    const maxLabel = formatMaxSize();

    for (const file of fileArray) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        rejections.push({ name: file.name, reason: 'unsupported type' });
        continue;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        rejections.push({ name: file.name, reason: `exceeds ${maxLabel} limit` });
        continue;
      }

      const id = crypto.randomUUID();
      const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
      newUploads.push({ id, file, progress: 0, preview });
    }

    if (rejections.length > 0) {
      const details = rejections.map((r) => `${r.name}: ${r.reason}`).join('\n');
      toast.error(`Some files were skipped:\n${details}`);
    }

    if (newUploads.length === 0) return;

    setUploads((prev) => [...prev, ...newUploads]);
    await runPool(newUploads, runUpload, UPLOAD_MAX_CONCURRENT_FILES);
  }, []);

  // Upload a single item end-to-end; shared by processFiles and retryUpload.
  const runUpload = useCallback(async (u: UploadingFile) => {
    // Progress setter that never moves backwards (parallel part events can race).
    const setProgress = (id: string, pct: number) => {
      const clamped = Math.min(99, Math.max(0, Math.round(pct)));
      setUploads((prev) =>
        prev.map((x) => (x.id === id ? { ...x, progress: Math.max(x.progress, clamped) } : x)),
      );
    };

    try {
      const file = await maybeDownscaleImage(u.file);
      setUploads((prev) => prev.map((x) => (x.id === u.id ? { ...x, progress: 3 } : x)));

      const attachmentId =
        file.size > MULTIPART_THRESHOLD_BYTES
          ? await uploadMultipart(file, (p) => setProgress(u.id, p))
          : await uploadSinglePut(file, (p) => setProgress(u.id, p));

      setUploads((prev) =>
        prev.map((x) => (x.id === u.id ? { ...x, progress: 100, attachmentId } : x)),
      );
    } catch {
      setUploads((prev) =>
        prev.map((x) => (x.id === u.id ? { ...x, error: 'Upload failed' } : x)),
      );
    }
  }, []);

  /** Retry a failed upload in place (keeps the original picked file). */
  const retryUpload = useCallback(
    (id: string) => {
      const upload = uploadsRef.current.find((u) => u.id === id);
      if (!upload || !upload.error) return;
      setUploads((prev) =>
        prev.map((u) => (u.id === id ? { ...u, error: undefined, progress: 0 } : u)),
      );
      void runUpload({ ...upload, error: undefined, progress: 0 });
    },
    [runUpload],
  );

  const removeUpload = useCallback((id: string) => {
    setUploads((prev) => {
      const upload = prev.find((u) => u.id === id);
      if (upload?.preview) URL.revokeObjectURL(upload.preview);
      return prev.filter((u) => u.id !== id);
    });
  }, []);

  const clearUploads = useCallback(() => {
    setUploads((prev) => {
      prev.forEach((u) => {
        if (u.preview) URL.revokeObjectURL(u.preview);
      });
      return [];
    });
  }, []);

  const getReadyAttachmentIds = useCallback((): string[] => {
    return uploads
      .filter((u) => u.progress === 100 && u.attachmentId && !u.error)
      .map((u) => u.attachmentId!);
  }, [uploads]);

  const allReady = uploads.length > 0 && uploads.every((u) => u.progress === 100 && !u.error);
  const hasUploads = uploads.length > 0;

  return {
    uploads,
    setUploads,
    processFiles,
    retryUpload,
    removeUpload,
    clearUploads,
    getReadyAttachmentIds,
    allReady,
    hasUploads,
  };
}

export function FileUploadPreview({
  uploads,
  onRemove,
  onRetry,
}: {
  uploads: UploadingFile[];
  onRemove: (id: string) => void;
  onRetry?: (id: string) => void;
}) {
  if (uploads.length === 0) return null;

  return (
    <div className="flex gap-2 overflow-x-auto border-t border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-800/50">
      {uploads.map((upload) => (
        <div key={upload.id} className="relative flex-shrink-0">
          {upload.preview ? (
            <img
              src={upload.preview}
              alt={upload.file.name}
              className="h-16 w-16 rounded-lg object-cover"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-gray-200 dark:bg-gray-700">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {upload.file.name.split('.').pop()?.toUpperCase()}
              </span>
            </div>
          )}

          {/* Progress overlay */}
          {upload.progress < 100 && !upload.error && (
            <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40">
              <div className="h-1.5 w-10 overflow-hidden rounded-full bg-gray-300">
                <div
                  className="h-full bg-blue-500 transition-all"
                  style={{ width: `${upload.progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Error overlay with retry */}
          {upload.error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 rounded-lg bg-red-500/50">
              {onRetry ? (
                <button
                  onClick={() => onRetry(upload.id)}
                  aria-label={`Retry uploading ${upload.file.name}`}
                  className="flex items-center gap-1 rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-semibold text-red-600 hover:bg-white"
                >
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M5.5 9a7 7 0 0 1 12.8-1.5M18.5 15a7 7 0 0 1-12.8 1.5" />
                  </svg>
                  Retry
                </button>
              ) : (
                <span className="text-xs font-medium text-white">!</span>
              )}
            </div>
          )}

          {/* Remove button */}
          <button
            onClick={() => onRemove(upload.id)}
            className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-gray-700 text-white hover:bg-gray-600"
          >
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}
